/** PointerInput — Drag-anchor 式の 8 方向操作(paper.io 2 / Royal Match モバイル準拠)。
 *
 *  挙動:
 *    - pointerdown で **アンカー位置**を記録(指 or マウスの位置)
 *    - pointermove で アンカーから現在位置への delta ベクトル → 8 方向にスナップ → onDirection 発火
 *    - pointerup でアンカー解除(プレイヤーは最後の方向を維持して走り続ける)
 *
 *  斜め移動なめらか化(Phase A2 Day 8 追加):
 *    1. **EMA 平滑化**: 生の dx/dy を低域通過フィルタで吸収(指ジッターを除去)
 *    2. **ヒステリシス**: 現在方向の中央 ±(22.5° + 7°)に居る間は方向を変えない
 *       → 約 30° の中途半端な角度で「右 ↔ 右上」を行き来する階段状の動きを防止
 *    3. **dead-zone は短く**(6px)、引き始めから即反応
 *    4. onDragStart / onDragMove / onDragEnd の callback を提供 → 視覚インジケータを描画可能
 *
 *  Reference: docs/STAGE_DESIGN.md §1.1 8-directional, CLAUDE.md タッチ UX 改善要求
 */

import type { Direction } from '../core/types';

export interface PointerInputOptions {
  readonly canvas: HTMLCanvasElement;
  readonly onDirection: (dir: Direction) => void;
  /** drag 開始(touchstart / mousedown 直後) */
  readonly onDragStart?: (clientX: number, clientY: number) => void;
  /** drag 中の delta + 距離(視覚フィードバック描画用) */
  readonly onDragMove?: (deltaX: number, deltaY: number, distance: number) => void;
  /** drag 終了(touchend / mouseup / cancel) */
  readonly onDragEnd?: () => void;
}

/** dead-zone(短いほど即応性が増す)。5-6px がスマホ実機でしっくりくる体感値。 */
const ANCHOR_DEAD_ZONE_PX = 6;

/** EMA 平滑化係数(0..1、低いほど滑らか、高いほど即応)。
 *  0.4 ≈ 約 2-3 サンプル平均で、指のミクロ振動だけを除去し、意図的な方向転換は速く反映。 */
const SMOOTH_ALPHA = 0.4;

/** ヒステリシス角(度)。
 *  現在方向の中央 ± (22.5° + HYSTERESIS_DEG) に居る間は方向を維持。
 *  7° は触感上「粘る」が「重くない」絶妙な値(Royal Match / クラロワ系のフィーリング)。 */
const HYSTERESIS_DEG = 7;
const HYSTERESIS_RAD = (HYSTERESIS_DEG * Math.PI) / 180;

/** 8 セグメント角度マップ。-π〜π を 8 等分してインデックス化。 */
const SEGMENT_TO_DIRECTION: Record<number, Direction> = {
  [-4]: 'left',
  [-3]: 'upLeft',
  [-2]: 'up',
  [-1]: 'upRight',
  0: 'right',
  1: 'downRight',
  2: 'down',
  3: 'downLeft',
  4: 'left',
};

/** 各 Direction の代表角度(画面座標、y は下向き正)。ヒステリシス判定に使用。 */
const DIRECTION_TO_ANGLE: Record<Direction, number> = {
  right: 0,
  downRight: Math.PI / 4,
  down: Math.PI / 2,
  downLeft: (3 * Math.PI) / 4,
  left: Math.PI,
  upLeft: -(3 * Math.PI) / 4,
  up: -Math.PI / 2,
  upRight: -Math.PI / 4,
};

/** 2 つの角度の最短角差(絶対値、0..π)を返す。 */
function angularDistance(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return Math.abs(d);
}

export function attachPointerInput(options: PointerInputOptions): () => void {
  const { canvas, onDirection, onDragStart, onDragMove, onDragEnd } = options;

  let anchor: { x: number; y: number } | null = null;
  let lastDirection: Direction | null = null;
  let activePointerId: number | null = null;
  /** setPointerCapture が成功したか。成功時はキャンバス外スワイプでも pointer が追従する。
   *  失敗時(iOS Safari の一部条件)のみ pointerleave をドラッグ終了として扱う。 */
  let pointerCaptured = false;

  // EMA 平滑化用の内部状態(各 drag セッションで reset)
  let smoothDx = 0;
  let smoothDy = 0;
  let smoothInited = false;

  const updateDirection = (clientX: number, clientY: number): void => {
    if (!anchor) return;
    const rawDx = clientX - anchor.x;
    const rawDy = clientY - anchor.y;

    // EMA で指のミクロ振動を吸収。最初のサンプルは raw を採用して初期遅延ゼロ。
    if (!smoothInited) {
      smoothDx = rawDx;
      smoothDy = rawDy;
      smoothInited = true;
    } else {
      smoothDx = SMOOTH_ALPHA * rawDx + (1 - SMOOTH_ALPHA) * smoothDx;
      smoothDy = SMOOTH_ALPHA * rawDy + (1 - SMOOTH_ALPHA) * smoothDy;
    }

    const dist = Math.hypot(smoothDx, smoothDy);

    // 視覚フィードバック(drag stick 等)は raw 値・raw 距離で統一:
    // 「ハンドルが大きく動いているのに距離が小さい」体感的不一致を防ぐ。
    onDragMove?.(rawDx, rawDy, Math.hypot(rawDx, rawDy));

    if (dist < ANCHOR_DEAD_ZONE_PX) return;

    const angle = Math.atan2(smoothDy, smoothDx);

    // ヒステリシス: 現在方向の中央 ± (22.5° + HYSTERESIS) に居る間は方向維持。
    // 旧実装(単純 Math.round スナップ)では、22.5° ぴったりの境界で指ジッターが
    // 「右 ↔ 右上」を行き来し階段状の動きを生んでいた。
    if (lastDirection !== null) {
      const currentAngle = DIRECTION_TO_ANGLE[lastDirection];
      const dist2 = angularDistance(angle, currentAngle);
      if (dist2 <= Math.PI / 4 + HYSTERESIS_RAD) {
        return; // 現在方向のテリトリーに居る → 何もしない
      }
    }

    const segment = Math.round(angle / (Math.PI / 4));
    const newDir = SEGMENT_TO_DIRECTION[segment];
    if (!newDir) return;
    if (newDir === lastDirection) return;
    lastDirection = newDir;
    onDirection(newDir);
  };

  const onPointerDown = (event: PointerEvent): void => {
    // 既に別の指で操作中なら無視(マルチタッチ干渉防止)
    if (activePointerId !== null) return;
    activePointerId = event.pointerId;
    anchor = { x: event.clientX, y: event.clientY };
    // 新セッション開始 → 平滑化を初期化
    smoothInited = false;
    smoothDx = 0;
    smoothDy = 0;
    onDragStart?.(event.clientX, event.clientY);

    pointerCaptured = false;
    try {
      canvas.setPointerCapture(event.pointerId);
      pointerCaptured = canvas.hasPointerCapture(event.pointerId);
    } catch {
      /* setPointerCapture が失敗してもロジックは動く(pointerCaptured = false のまま) */
    }
    event.preventDefault();
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (activePointerId !== event.pointerId) return;
    updateDirection(event.clientX, event.clientY);
  };

  const onPointerUp = (event: PointerEvent): void => {
    if (activePointerId !== event.pointerId) return;
    anchor = null;
    activePointerId = null;
    pointerCaptured = false;
    // lastDirection はクリア:再 drag 開始時に同方向でも emit するため
    lastDirection = null;
    // 平滑化もリセット
    smoothInited = false;
    smoothDx = 0;
    smoothDy = 0;
    onDragEnd?.();
    try {
      if (canvas.hasPointerCapture(event.pointerId)) {
        canvas.releasePointerCapture(event.pointerId);
      }
    } catch {
      /* noop */
    }
  };

  /** pointerleave: pointer がキャンバス領域外へ出たとき。
   *  setPointerCapture が成功している場合はキャプチャ中に発火しないので無視。
   *  キャプチャ失敗時(iOS Safari の一部)のみ、ドラッグ終了として扱う。
   *  → これでキャンバス端を掠める大回りスワイプ中に操作が切れる致死バグを防ぐ。 */
  const onPointerLeave = (event: PointerEvent): void => {
    if (activePointerId !== event.pointerId) return;
    if (pointerCaptured) return; // キャプチャ成功時は何もしない(pointer は追従し続ける)
    onPointerUp(event);
  };

  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerUp);
  canvas.addEventListener('pointerleave', onPointerLeave);

  return (): void => {
    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointercancel', onPointerUp);
    canvas.removeEventListener('pointerleave', onPointerLeave);
  };
}
